import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PillToggle } from "@/components/admin/PillToggle";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical, ExternalLink, Upload, X, FolderOpen, Eye, EyeOff, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { mapDatabaseError } from "@/lib/errorUtils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Import fallback images
import hyundaiLogo from "@/assets/clients/hyundai.png";
import rogersLogo from "@/assets/clients/rogers.png";
import bravoLogo from "@/assets/clients/bravo.png";
import craLogo from "@/assets/clients/cra.jpg";
import electionsCanadaLogo from "@/assets/clients/elections-canada.jpg";
import ottawaFoodBankLogo from "@/assets/clients/ottawa-food-bank.png";
import canadianBloodServicesLogo from "@/assets/clients/canadian-blood-services.jpg";
import phacLogo from "@/assets/clients/phac.png";
import firstAirLogo from "@/assets/clients/first-air.png";
import justePourRireLogo from "@/assets/clients/juste-pour-rire.png";
import hondaLogo from "@/assets/clients/honda.png";
import pipscLogo from "@/assets/clients/pipsc.png";
import omaLogo from "@/assets/clients/oma.png";
import casmLogo from "@/assets/clients/casm.png";
import primeMinisterLogo from "@/assets/clients/prime-minister.jpg";
import hpascLogo from "@/assets/clients/hpasc.png";
import balestraLogo from "@/assets/clients/balestra.jpg";
import logoPlaceholder from "@/assets/logo-placeholder.svg";

const fallbackLogos: Record<string, string> = {
  "Hyundai": hyundaiLogo,
  "Honda": hondaLogo,
  "Rogers": rogersLogo,
  "CRA": craLogo,
  "Elections Canada": electionsCanadaLogo,
  "Canadian Blood Services": canadianBloodServicesLogo,
  "Public Health Agency of Canada": phacLogo,
  "First Air": firstAirLogo,
  "Juste Pour Rire": justePourRireLogo,
  "Ottawa Food Bank": ottawaFoodBankLogo,
  "Bravo": bravoLogo,
  "PIPSC": pipscLogo,
  "Ontario Medical Association": omaLogo,
  "Canada Aviation and Space Museum": casmLogo,
  "Prime Minister's Office": primeMinisterLogo,
  "Health Products and Food Branch": hpascLogo,
  "Balestra Productions": balestraLogo,
};

type Client = {
  id: string;
  name: string;
  logo_url: string | null;
  website_url: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  testimonial: string | null;
  display_order: number;
  is_active: boolean;
};

const emptyClient = {
  name: "",
  logo_url: "",
  website_url: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  testimonial: "",
  display_order: 0,
  is_active: true,
};

// Sortable row component
interface SortableClientRowProps {
  client: Client;
  logoUrl: string | null;
  onImageError: (name: string) => void;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string, isActive: boolean) => void;
}

const SortableClientRow = ({ client, logoUrl, onImageError, onEdit, onDelete, onToggleVisibility }: SortableClientRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className={isDragging ? "z-50" : ""}>
      <CardContent className="flex items-center gap-4 p-4">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="text-muted-foreground" />
        </div>
        <div className="w-16 h-12 bg-muted rounded flex items-center justify-center overflow-hidden">
          <img
            src={logoUrl || logoPlaceholder}
            alt={client.name}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              e.currentTarget.src = logoPlaceholder;
              onImageError(client.name);
            }}
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${!client.is_active ? "text-muted-foreground" : ""}`}>
              {client.name}
            </h3>
            {!client.is_active && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                Hidden
              </span>
            )}
          </div>
          {client.website_url && (
            <a
              href={client.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <ExternalLink size={12} />
              {client.website_url.replace(/^https?:\/\//, "").split("/")[0]}
            </a>
          )}
        </div>
        <div className="flex items-center gap-1">
          {client.testimonial && (
            <span title="Has testimonial">
              <MessageSquareQuote size={16} className="text-primary/60" />
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleVisibility(client.id, !client.is_active)}
            title={client.is_active ? "Hide client" : "Show client"}
          >
            {client.is_active ? <Eye size={16} /> : <EyeOff size={16} className="text-muted-foreground" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onEdit(client)}>
            <Pencil size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm("Delete this client?")) {
                onDelete(client.id);
              }
            }}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientsAdmin = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState(emptyClient);
  const [isUploading, setIsUploading] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [showTestimonials, setShowTestimonials] = useState(true);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getLogoUrl = (client: { name: string; logo_url: string | null }) => {
    if (failedImages.has(client.name)) {
      return fallbackLogos[client.name] || null;
    }
    if (client.logo_url) {
      return client.logo_url;
    }
    return fallbackLogos[client.name] || null;
  };

  const handleImageError = (clientName: string) => {
    setFailedImages(prev => new Set(prev).add(clientName));
  };

  const handleLogoUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `clients/${formData.name.toLowerCase().replace(/\s+/g, "-") || Date.now()}-${Date.now()}.${fileExt}`;
      
      if (formData.logo_url?.includes("media")) {
        const oldPath = formData.logo_url.split("/media/")[1];
        if (oldPath) {
          await supabase.storage.from("media").remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from("media")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("media")
        .getPublicUrl(fileName);

      setFormData({ ...formData, logo_url: publicUrl });
      toast.success("Logo uploaded");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!formData.logo_url) return;
    
    try {
      if (formData.logo_url.includes("media")) {
        const path = formData.logo_url.split("/media/")[1];
        if (path) {
          await supabase.storage.from("media").remove([path]);
        }
      }
      setFormData({ ...formData, logo_url: "" });
      toast.success("Logo removed");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    }
  };

  const { data: clients, isLoading } = useQuery({
    queryKey: ["admin-clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data as Client[];
    },
  });

  // Fetch testimonials visibility setting
  const { data: testimonialsSetting } = useQuery({
    queryKey: ["site-settings", "show_testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "show_testimonials")
        .maybeSingle();
      if (error) throw error;
      return data?.value as boolean | null;
    },
  });

  // Sync local state with fetched setting
  useEffect(() => {
    if (testimonialsSetting !== undefined && testimonialsSetting !== null) {
      setShowTestimonials(testimonialsSetting);
    }
  }, [testimonialsSetting]);

  // Mutation to update testimonials visibility
  const toggleTestimonialsMutation = useMutation({
    mutationFn: async (show: boolean) => {
      const { data: existing } = await supabase
        .from("site_settings")
        .select("id")
        .eq("key", "show_testimonials")
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value: show })
          .eq("key", "show_testimonials");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ key: "show_testimonials", value: show });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings", "show_testimonials"] });
      toast.success(showTestimonials ? "Testimonials section visible" : "Testimonials section hidden");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
      // Revert local state on error
      setShowTestimonials(!showTestimonials);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData & { id?: string }) => {
      if (data.id) {
        const { error } = await supabase
          .from("clients")
          .update(data)
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("clients").insert(data);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clients"] });
      setIsDialogOpen(false);
      setEditingClient(null);
      setFormData(emptyClient);
      toast.success(editingClient ? "Client updated" : "Client created");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clients"] });
      toast.success("Client deleted");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("clients")
        .update({ is_active: isActive })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clients"] });
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; display_order: number }[]) => {
      for (const update of updates) {
        const { error } = await supabase
          .from("clients")
          .update({ display_order: update.display_order })
          .eq("id", update.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-clients"] });
      toast.success("Order updated");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id && clients) {
      const oldIndex = clients.findIndex((c) => c.id === active.id);
      const newIndex = clients.findIndex((c) => c.id === over.id);
      
      const reorderedClients = arrayMove(clients, oldIndex, newIndex);
      const updates = reorderedClients.map((client, index) => ({
        id: client.id,
        display_order: index + 1,
      }));
      
      reorderMutation.mutate(updates);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      logo_url: client.logo_url || "",
      website_url: client.website_url || "",
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      testimonial: client.testimonial || "",
      display_order: client.display_order,
      is_active: client.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingClient(null);
    setFormData(emptyClient);
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      ...formData,
      id: editingClient?.id,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold">Clients</h1>
          <p className="text-muted-foreground mt-1">
            Manage client logos displayed on the website.
          </p>
        </div>
      </div>

      {/* Testimonials Section Toggle */}
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <h3 className="font-medium">Testimonials Section</h3>
            <p className="text-sm text-muted-foreground">
              Show or hide the testimonials section on the home page
            </p>
          </div>
          <PillToggle
            checked={showTestimonials}
            onCheckedChange={(checked) => {
              setShowTestimonials(checked);
              toggleTestimonialsMutation.mutate(checked);
            }}
            labelOn="Show"
            labelOff="Hide"
            disabled={toggleTestimonialsMutation.isPending}
          />
        </CardContent>
      </Card>

      <Separator />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium">Client List</h2>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus size={18} className="mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingClient ? "Edit Client" : "Add New Client"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="block mb-2">Logo</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                    }}
                  />
                  {(() => {
                    const previewUrl = formData.logo_url || fallbackLogos[formData.name] || null;
                    return previewUrl ? (
                      <div className="relative inline-block">
                        <div className="p-4 bg-muted rounded-lg">
                          <img
                            src={previewUrl}
                            alt="Logo preview"
                            className="max-h-16 object-contain"
                            onError={(e) => {
                              const fallback = fallbackLogos[formData.name];
                              if (fallback && e.currentTarget.src !== fallback) {
                                e.currentTarget.src = fallback;
                              }
                            }}
                          />
                        </div>
                        {formData.logo_url && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={handleLogoDelete}
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                        >
                          <Upload size={16} className="mr-2" />
                          {isUploading ? "Uploading..." : "Upload"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsMediaPickerOpen(true)}
                        >
                          <FolderOpen size={16} className="mr-2" />
                          Browse Library
                        </Button>
                      </div>
                    );
                  })()}
                  {(formData.logo_url || fallbackLogos[formData.name]) && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                      >
                        {formData.logo_url ? "Replace" : "Upload New"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsMediaPickerOpen(true)}
                      >
                        Browse Library
                      </Button>
                    </div>
                  )}
                  <MediaPicker
                    open={isMediaPickerOpen}
                    onOpenChange={setIsMediaPickerOpen}
                    onSelect={(url) => setFormData({ ...formData, logo_url: url })}
                    bucket="media"
                    folder="clients"
                    onlyFolder
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    value={formData.website_url}
                    onChange={(e) =>
                      setFormData({ ...formData, website_url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Contact Name</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) =>
                          setFormData({ ...formData, contact_name: e.target.value })
                        }
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) =>
                          setFormData({ ...formData, contact_email: e.target.value })
                        }
                        placeholder="contact@company.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Phone</Label>
                      <Input
                        id="contact_phone"
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) =>
                          setFormData({ ...formData, contact_phone: e.target.value })
                        }
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="testimonial">Testimonial</Label>
                  <Textarea
                    id="testimonial"
                    value={formData.testimonial}
                    onChange={(e) =>
                      setFormData({ ...formData, testimonial: e.target.value })
                    }
                    placeholder="What did this client say about working with you?"
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Visible</Label>
                    <PillToggle
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                      labelOn="Yes"
                      labelOff="No"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="display_order">Order</Label>
                    <Input
                      id="display_order"
                      type="number"
                      value={formData.display_order}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          display_order: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-20"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : clients && clients.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={clients.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {clients.map((client) => (
                <SortableClientRow
                  key={client.id}
                  client={client}
                  logoUrl={getLogoUrl(client)}
                  onImageError={handleImageError}
                  onEdit={handleEdit}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  onToggleVisibility={(id, isActive) => toggleVisibilityMutation.mutate({ id, isActive })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No clients added yet.</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus size={18} className="mr-2" />
              Add Your First Client
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientsAdmin;
